import {
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Autocomplete,
  TextInput,
  Select,
  Badge,
  Group,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { z } from "zod";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShield,
  faCapsule,
  faFileXmark,
  faLock,
} from "@fortawesome/sharp-regular-svg-icons";
import { gql } from "@/__generated__";
import {
  SaveInsuranceInfoInput,
  SubDenialReason,
} from "@/__generated__/graphql";
import Loader from "@/components/shared/Loader";
import { InsuranceCompany, PBM } from "shared/src/data/insurance-companies";
import { insuranceInfoValidationSchema } from "shared/src/validations/insurance-validation";
import { Alert } from "design-system/src/components/Alert";
import { Button } from "@/components/buttons/Button/Button";
import { useFormDirtyTracking } from "@/hooks/useFormDirtyTracking";
import { subDenialReasonOptionsByCondition } from "shared/src/data/sub-denial-reasons";
import styles from "./insurance-info-form.module.scss";

const GET_APPEAL_INSURANCE = gql(`
  query GetAppealInsurance($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      patientInsurance {
        providerName
        planCategory
        employer {
          employerName
        }
        memberNumber
        groupNumber
        rxGroupNumber
        rxMemberId
        rxBinNumber
        pbmName
        isPatientEnrollee
        enrolleeFirstName
        enrolleeLastName
        denyingParty
        otherDenyingPartyName
      }
      denial {
        denialReason
        subDenialReasons
      }
    }
  }
`);

const SAVE_INSURANCE_INFO = gql(`
  mutation SaveInsuranceInfo($appealId: ID!, $insuranceInfo: SaveInsuranceInfoInput!) {
    saveInsuranceInfo(appealId: $appealId, insuranceInfo: $insuranceInfo)
  }
`);

export type InsuranceInfoFormValues = {
  insuranceProvider: string;
  planCategory: string;
  employerName: string;
  memberId: string;
  groupNumber: string;
  rxGroup: string;
  rxBin: string;
  rxMemberId: string;
  pharmacyBenefitManager: string;
  relationshipToPolicyholder: string;
  policyholderFirstName: string;
  policyholderLastName: string;
  denyingParty: string;
  otherDenyingPartyName: string;
  denialReason: string;
  subDenialReasons: SubDenialReason[];
};

type InsuranceInfoFormProps = {
  appealId: string;
  readOnly?: boolean;
};

export type InsuranceInfoFormHandle = {
  submit: () => Promise<boolean>;
};

const requiresEmployerName = (planCategory: string) => {
  return (
    planCategory === "EMPLOYER_SELF_FUNDED" ||
    planCategory === "EMPLOYER_FULLY_INSURED"
  );
};

const requiresPolicyholderNames = (relationshipToPolicyholder: string) => {
  return relationshipToPolicyholder !== "SELF";
};

const requiresOtherDenyingPartyName = (denyingParty: string) => {
  return denyingParty === "OTHER";
};

const mapEnrolleeStatusToRelationship = (
  isPatientEnrollee: boolean | null | undefined,
): string => {
  if (isPatientEnrollee === true) return "SELF";
  if (isPatientEnrollee === false) return "SPOUSE";
  return "";
};

const mapFormValuesToMutationInput = (
  values: InsuranceInfoFormValues,
): SaveInsuranceInfoInput => {
  const input: SaveInsuranceInfoInput = {
    insuranceProvider: values.insuranceProvider || undefined,
    memberId: values.memberId || undefined,
    groupNumber: values.groupNumber || undefined,
    planCategory: (values.planCategory || undefined) as any,
    denyingParty: (values.denyingParty || undefined) as any,
    relationshipToPolicyholder: (values.relationshipToPolicyholder ||
      undefined) as any,
    pharmacyBenefitManager: values.pharmacyBenefitManager || undefined,
    rxBin: values.rxBin || undefined,
    rxGroup: values.rxGroup || undefined,
    rxMemberId: values.rxMemberId || undefined,
    employerName: requiresEmployerName(values.planCategory)
      ? values.employerName || undefined
      : undefined,
    policyholderFirstName: requiresPolicyholderNames(
      values.relationshipToPolicyholder,
    )
      ? values.policyholderFirstName || undefined
      : undefined,
    policyholderLastName: requiresPolicyholderNames(
      values.relationshipToPolicyholder,
    )
      ? values.policyholderLastName || undefined
      : undefined,
    otherDenyingPartyName: requiresOtherDenyingPartyName(values.denyingParty)
      ? values.otherDenyingPartyName || undefined
      : undefined,
  };

  return input;
};

const insuranceCompanies = Object.values(InsuranceCompany)
  .sort()
  .map((c) => ({ label: c, value: c }));
const pbms = Object.values(PBM)
  .sort()
  .map((c) => ({ label: c, value: c }));

const PLAN_CATEGORY_OPTIONS = [
  { value: "EMPLOYER_SELF_FUNDED", label: "Employer Self-Funded" },
  { value: "EMPLOYER_FULLY_INSURED", label: "Employer Fully-Insured" },
  {
    value: "EMPLOYER_LOCAL_STATE_GOVERNMENT",
    label: "Employer Local/State Government",
  },
  {
    value: "EMPLOYER_FEDERAL_GOVERNMENT",
    label: "Employer Federal Government",
  },
  { value: "INDIVIDUAL_EXCHANGE", label: "Individual Exchange" },
  { value: "MEDICARE_ADVANTAGE", label: "Medicare Advantage" },
  { value: "MEDICARE_TRADITIONAL", label: "Medicare Traditional" },
  { value: "MEDICAID", label: "Medicaid" },
];

const POLICYHOLDER_RELATIONSHIP_OPTIONS = [
  { value: "SELF", label: "Self" },
  { value: "SPOUSE", label: "Spouse" },
  { value: "DEPENDENT", label: "Dependent" },
];

const DENYING_PARTY_OPTIONS = [
  { value: "INSURANCE_COMPANY", label: "Insurance Company" },
  { value: "PBM", label: "Pharmacy Benefit Manager" },
  { value: "OTHER", label: "Other" },
];

const InsuranceInfoForm = forwardRef<
  InsuranceInfoFormHandle,
  InsuranceInfoFormProps
>(({ appealId, readOnly = false }, ref) => {
  const [mutationError, setMutationError] = useState(false);

  const form = useForm<InsuranceInfoFormValues>({
    initialValues: {
      insuranceProvider: "",
      planCategory: "",
      employerName: "",
      memberId: "",
      groupNumber: "",
      rxGroup: "",
      rxBin: "",
      rxMemberId: "",
      pharmacyBenefitManager: "",
      relationshipToPolicyholder: "",
      policyholderFirstName: "",
      policyholderLastName: "",
      denyingParty: "",
      otherDenyingPartyName: "",
      denialReason: "",
      subDenialReasons: [],
    },
    validate: zod4Resolver(
      insuranceInfoValidationSchema.and(
        z.object({
          denialReason: z.string().optional(),
        }),
      ),
    ),
  });

  const { resetFormDirty } = useFormDirtyTracking(form);

  const {
    data: insuranceQueryData,
    loading: insuranceQueryLoading,
    error: insuranceQueryError,
  } = useQuery(GET_APPEAL_INSURANCE, {
    variables: { appealId },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  const [saveInsuranceInfo] = useMutation(SAVE_INSURANCE_INFO);

  useEffect(() => {
    if (insuranceQueryData?.getAppealById) {
      const info = insuranceQueryData.getAppealById.patientInsurance;
      const denial = insuranceQueryData.getAppealById.denial;

      const valuesToSet = {
        insuranceProvider: info?.providerName || "",
        planCategory: info?.planCategory || "",
        employerName: info?.employer?.employerName || "",
        memberId: info?.memberNumber || "",
        groupNumber: info?.groupNumber || "",
        rxGroup: info?.rxGroupNumber || "",
        rxBin: info?.rxBinNumber || "",
        rxMemberId: info?.rxMemberId || "",
        pharmacyBenefitManager: info?.pbmName || "",
        relationshipToPolicyholder: mapEnrolleeStatusToRelationship(
          info?.isPatientEnrollee,
        ),
        policyholderFirstName: info?.enrolleeFirstName || "",
        policyholderLastName: info?.enrolleeLastName || "",
        denyingParty: info?.denyingParty || "",
        otherDenyingPartyName: info?.otherDenyingPartyName || "",
        denialReason: denial?.denialReason || "",
        subDenialReasons: denial?.subDenialReasons || [],
      };

      form.setInitialValues(valuesToSet);
      form.reset();
    }
  }, [insuranceQueryData]);

  useEffect(() => {
    if (!requiresEmployerName(form.values.planCategory)) {
      form.setFieldValue("employerName", "");
    }
  }, [form.values.planCategory]);

  useEffect(() => {
    if (form.values.relationshipToPolicyholder === "SELF") {
      form.setFieldValue("policyholderFirstName", "");
      form.setFieldValue("policyholderLastName", "");
    }
  }, [form.values.relationshipToPolicyholder]);

  useEffect(() => {
    if (form.values.denyingParty !== "OTHER") {
      form.setFieldValue("otherDenyingPartyName", "");
    }
  }, [form.values.denyingParty]);

  const clearInsuranceGroup = () => {
    form.setFieldValue("insuranceProvider", "");
    form.setFieldValue("memberId", "");
    form.setFieldValue("groupNumber", "");
    form.setFieldValue("planCategory", null as unknown as string);
    form.setFieldValue("relationshipToPolicyholder", null as unknown as string);
    form.setFieldValue("employerName", "");
    form.setFieldValue("policyholderFirstName", "");
    form.setFieldValue("policyholderLastName", "");
    form.validate();
  };

  const clearDenialGroup = () => {
    form.setFieldValue("denyingParty", null as unknown as string);
    form.setFieldValue("otherDenyingPartyName", "");
    form.validate();
  };

  const handleFormSubmit = async (
    values: InsuranceInfoFormValues,
  ): Promise<boolean> => {
    const mutationInput = mapFormValuesToMutationInput(values);

    try {
      setMutationError(false);
      await saveInsuranceInfo({
        variables: {
          appealId,
          insuranceInfo: mutationInput,
        },
      });

      resetFormDirty();
      return true;
    } catch {
      setMutationError(true);
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const validationResult = form.validate();
      if (validationResult.hasErrors) {
        return false;
      }
      return await handleFormSubmit(form.getValues());
    },
  }));

  const showEmployerNameField = useMemo(() => {
    return requiresEmployerName(form.values.planCategory);
  }, [form.values.planCategory]);

  const showPolicyholderNameFields = useMemo(() => {
    return (
      form.values.relationshipToPolicyholder !== "SELF" &&
      form.values.relationshipToPolicyholder !== ""
    );
  }, [form.values.relationshipToPolicyholder]);

  const showOtherDenyingPartyField = useMemo(() => {
    return form.values.denyingParty === "OTHER";
  }, [form.values.denyingParty]);

  if (insuranceQueryLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader color="loader_almost_black" width="36" height="36" />
      </div>
    );
  }

  const hasError = insuranceQueryError || mutationError;

  return (
    <>
      {hasError && (
        <div className={styles.errorContainer}>
          <Alert variant="error">
            <Alert.Title>An error occurred. Please try again.</Alert.Title>
          </Alert>
        </div>
      )}
      <form className={styles.container} onSubmit={(e) => e.preventDefault()}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon icon={faShield} className={styles.sectionIcon} />
              Patient Insurance Information
            </h3>
            <div className={styles.sectionSubtitle}>
              Provide the patient's insurance information
            </div>
          </div>
          {form.errors.insuranceGroup && (
            <Alert variant="error">
              <Alert.Caption>
                <span>
                  {form.errors.insuranceGroup as string} or{" "}
                  <Button
                    variant="tertiary"
                    size="2xs"
                    type="button"
                    onClick={clearInsuranceGroup}
                    data-testid="clear-insurance-group"
                  >
                    Clear this section
                  </Button>{" "}
                  to continue
                </span>
              </Alert.Caption>
            </Alert>
          )}

          <div className={styles.formSection}>
            <Autocomplete
              label="Insurance Provider"
              data={insuranceCompanies}
              withAsterisk={false}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="insurance-provider-input"
              {...form.getInputProps("insuranceProvider")}
              className={styles.field}
            />

            <div className={styles.fieldRow}>
              <TextInput
                label="Member ID"
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="member-number-input"
                {...form.getInputProps("memberId")}
                className={styles.field}
              />

              <TextInput
                label="Group Number"
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="group-number-input"
                {...form.getInputProps("groupNumber")}
                className={styles.field}
              />
            </div>

            <Select
              label="Plan Type"
              placeholder="Select one"
              withAsterisk={false}
              data={PLAN_CATEGORY_OPTIONS}
              searchable
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="plan-category-input"
              {...form.getInputProps("planCategory")}
              className={styles.field}
            />

            {showEmployerNameField && (
              <TextInput
                label="Employer Name"
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                {...form.getInputProps("employerName")}
                className={styles.field}
              />
            )}

            <Select
              label="Patient relationship to policy holder"
              placeholder="Select one"
              withAsterisk={false}
              data={POLICYHOLDER_RELATIONSHIP_OPTIONS}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="policyholder-relationship-input"
              {...form.getInputProps("relationshipToPolicyholder")}
              className={styles.field}
            />

            {showPolicyholderNameFields && (
              <div className={styles.fieldRow}>
                <TextInput
                  label="Policyholder First Name"
                  withAsterisk={false}
                  readOnly={readOnly}
                  disabled={readOnly}
                  {...form.getInputProps("policyholderFirstName")}
                  className={styles.field}
                />

                <TextInput
                  label="Policyholder Last Name"
                  withAsterisk={false}
                  readOnly={readOnly}
                  disabled={readOnly}
                  {...form.getInputProps("policyholderLastName")}
                  className={styles.field}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon
                icon={faCapsule}
                className={styles.sectionIcon}
              />
              PBM Information
            </h3>
            <div className={styles.sectionSubtitle}>
              Provide the patient's pharmacy benefit manager details
            </div>
          </div>

          <div className={styles.formSection}>
            <Autocomplete
              label="Pharmacy Benefit Manager"
              data={pbms}
              withAsterisk={false}
              readOnly={readOnly}
              disabled={readOnly}
              {...form.getInputProps("pharmacyBenefitManager")}
              className={styles.field}
            />

            <div className={styles.fieldRow}>
              <TextInput
                label="Rx BIN"
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                {...form.getInputProps("rxBin")}
                className={styles.field}
              />

              <TextInput
                label="Rx Group"
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                {...form.getInputProps("rxGroup")}
                className={styles.field}
              />
            </div>

            <TextInput
              label="Rx Member ID"
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="rx-member-id-input"
              {...form.getInputProps("rxMemberId")}
              className={styles.field}
            />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon
                icon={faFileXmark}
                className={styles.sectionIcon}
              />
              Denial Information
            </h3>
            <div className={styles.sectionSubtitle}>
              Provide the patient's insurance information
            </div>
          </div>
          {form.errors.denialGroup && (
            <Alert variant="error">
              <Alert.Caption>
                <span>
                  {form.errors.denialGroup as string} or{" "}
                  <Button
                    variant="tertiary"
                    size="2xs"
                    type="button"
                    onClick={clearDenialGroup}
                    data-testid="clear-denial-group"
                  >
                    Clear this section
                  </Button>{" "}
                  to continue
                </span>
              </Alert.Caption>
            </Alert>
          )}

          <div className={styles.formSection}>
            <Select
              label="Denying Party"
              placeholder="Select one"
              withAsterisk={false}
              data={DENYING_PARTY_OPTIONS}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="denying-party-input"
              {...form.getInputProps("denyingParty")}
              className={styles.field}
            />

            {showOtherDenyingPartyField && (
              <TextInput
                label="Other Denying Party Name"
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                {...form.getInputProps("otherDenyingPartyName")}
                className={styles.field}
              />
            )}

            <TextInput
              label="Denial Reason"
              disabled
              {...form.getInputProps("denialReason")}
              className={styles.field}
              rightSection={
                <FontAwesomeIcon
                  icon={faLock}
                  style={{ color: "#666665", fontSize: "20px" }}
                />
              }
            />

            {form.values.subDenialReasons.length > 0 && (
              <Stack gap="xs" className={styles.field}>
                <label style={{ fontSize: "16px", fontWeight: 500 }}>
                  Denial Reason Details
                </label>
                <Group gap="xs">
                  {form.values.subDenialReasons.map((reason, index) => {
                    let label: string = reason;
                    for (const conditionReasons of Object.values(
                      subDenialReasonOptionsByCondition,
                    )) {
                      if (conditionReasons[reason]) {
                        label = conditionReasons[reason];
                        break;
                      }
                    }
                    return (
                      <Badge key={index} variant="light" color="gray" size="lg">
                        {label}
                      </Badge>
                    );
                  })}
                </Group>
              </Stack>
            )}
          </div>
        </div>
      </form>
    </>
  );
});

InsuranceInfoForm.displayName = "InsuranceInfoForm";

export default InsuranceInfoForm;
